import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Helper function to get user's timezone (replaces Intl.DateTimeFormat().resolvedOptions().timeZone)
function guessTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}
import { useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  EDVCDateContainer,
  EDVCParent,
  FloatingHeaderContainerDayCal,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { selectServiceProvidersData } from 'src/clinic/reducers/serviceProviders/serviceProvidersReducer'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { PageMainContentContainer } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import MentorHeader from 'src/mentor/components/MentorHeader'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import ScheduledPatientsTable from 'src/supportPortal/views/scheduledPatients/components/ScheduledPatientsTable'
import { Box, Chip, Paper, Typography, Divider } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

export default function MentorUpcomingAppointments() {
  const mentorProfileContextData = useContext(MentorProfileContext)
  console.log({ mentorProfileContextData })
  const { mentorClinicAssociationData, loadingAssociationsData } = mentorProfileContextData

  // Date range state management
  // Seed with "now" in user's timezone; passing only tz string produced invalid dates.
  const [selectedDate, setSelectedDate] = useState(dayjs().tz(guessTimezone()))
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs().add(10, 'days').tz(guessTimezone()).endOf('day'),
  )
  const [selectedPreset, setSelectedPreset] = useState('next10')
  const serviceProviders = useSelector(selectServiceProvidersData)
  const [selectedClinicId, setSelectedClinicId] = useState(null)
  const [selectedClinicBrandId, setSelectedClinicBrandId] = useState(null)

  // Calculate UTC formatted date strings for API calls
  const selectedDateUtcStartTime = useMemo(
    () => dayjs.utc(selectedDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
    [selectedDate],
  )
  const selectedDateUtcEndTime = useMemo(
    () => dayjs.utc(selectedEndDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
    [selectedEndDate],
  )

  const associatedClinicBrands = useMemo(() => {
    const uniqueBrands = new Map()

    mentorClinicAssociationData?.forEach((item) => {
      if (item?.clinicBrandId && !uniqueBrands.has(item.clinicBrandId)) {
        uniqueBrands.set(item.clinicBrandId, {
          id: item.clinicBrandId,
          brandName: item.clinicBrandName,
          title: item.clinicBrandName,
          ownerUserIds: item.clinicBrandOwnerUserIds,
        })
      }
    })

    return Array.from(uniqueBrands.values())
  }, [mentorClinicAssociationData])

  //   useEffect(() => {
  //     getAndSetManagersProfileData()
  //   }, [])

  function applyPreset(preset) {
    setSelectedPreset(preset)
    const now = dayjs().tz(guessTimezone()).startOf('day')
    switch (preset) {
      case 'today':
        setSelectedDate(now)
        setSelectedEndDate(now.endOf('day'))
        break
      case 'next7':
        setSelectedDate(now)
        setSelectedEndDate(now.add(7, 'day').endOf('day'))
        break
      case 'next30':
        setSelectedDate(now)
        setSelectedEndDate(now.add(30, 'day').endOf('day'))
        break
      default:
        setSelectedDate(now)
        setSelectedEndDate(now.add(10, 'day').endOf('day'))
        break
    }
  }

  function clearFilters() {
    setSelectedClinicBrandId(null)
    setSelectedClinicId(null)
    applyPreset('next10')
  }

  return (
    <>
      <MentorHeader
        heading="Upcoming Appointments"
        // component={
        //   <div className="d-flex gap-2 justify-content-center align-items-center">
        //     <GenericHeaderBackButton
        //       clickfunction={() => window.history.back()}
        //       styles={{ margin: '20px' }}
        //     />
        //     <HeaderGreeting main>Upcoming Appointments</HeaderGreeting>
        //   </div>
        // }
      />
      <PageMainContentContainer style={{ maxWidth: '1200px', marginTop: '16px', padding: '0 12px' }}>
        <EDVCParent>
          <div className="d-flex flex-column align-items-center gap-2 w-100">
            <FloatingHeaderContainerDayCal style={{ zIndex: 1 }} className="w-100">
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box flex={1} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                    <Box flex={1} minWidth={220}>
                      <CustomSelect
                        value={
                          associatedClinicBrands?.find(
                            (brand) => brand?.id === selectedClinicBrandId,
                          ) || null
                        }
                        options={associatedClinicBrands?.sort((a, b) =>
                          (a?.brandName || a?.title || '').localeCompare(
                            b?.brandName || b?.title || '',
                          ),
                        )}
                        label="Clinic Brand"
                        placeholder="All Brands"
                        getOptionValue={(option) => option?.id}
                        getOptionLabel={(option) => option?.brandName || option?.title}
                        selectType={selectTypes.object}
                        onChangeEvent={(e) => {
                          setSelectedClinicBrandId(e.target.value?.id || null)
                          setSelectedClinicId(null)
                        }}
                        id="clinicBrand"
                        isLoading={loadingAssociationsData}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2200 }),
                        }}
                      />
                    </Box>
                    <Box flex={1} minWidth={220}>
                      <CustomSelect
                        value={
                          mentorClinicAssociationData?.find(
                            (clinicLocation) => clinicLocation?.id === selectedClinicId,
                          ) || null
                        }
                        options={mentorClinicAssociationData
                          ?.filter((clinicLocation) => {
                            return selectedClinicBrandId
                              ? clinicLocation?.clinicBrandId === selectedClinicBrandId
                              : true
                          })
                          ?.sort((a, b) =>
                            (a?.clinicName || a?.title || '').localeCompare(
                              b?.clinicName || b?.title || '',
                            ),
                          )}
                        label="Clinic"
                        placeholder="All Clinics"
                        getOptionValue={(option) => option?.id}
                        getOptionLabel={(option) => option?.clinicName || option?.title}
                        selectType={selectTypes.object}
                        onChangeEvent={(e) => {
                          setSelectedClinicId(e.target.value?.id || null)
                        }}
                        id="clinic"
                        isLoading={loadingAssociationsData}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2200 }),
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                  <Box flex={1} display="flex" flexDirection="column" gap={1}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonthIcon fontSize="small" /> Date Range
                    </Typography>
                    <EDVCDateContainer style={{ padding: '6px 0' }}>
                      <div className="d-flex w-100 justify-content-center">
                        <DateRangePicker
                          clearIcon={null}
                          onChange={(val) => {
                            if (val?.[0] && val?.[1]) {
                              setSelectedPreset(null)
                              setSelectedDate(dayjs(val[0]))
                              setSelectedEndDate(dayjs(val[1]))
                            }
                          }}
                          value={[
                            selectedDate?.isValid?.() ? selectedDate.toDate() : new Date(),
                            selectedEndDate?.isValid?.() ? selectedEndDate.toDate() : new Date(),
                          ]}
                        />
                      </div>
                    </EDVCDateContainer>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {[
                        { id: 'today', label: 'Today' },
                        { id: 'next7', label: 'Next 7 days' },
                        { id: 'next10', label: 'Next 10 days' },
                        { id: 'next30', label: 'Next 30 days' },
                      ].map((preset) => (
                        <Chip
                          key={preset.id}
                          label={preset.label}
                          color={selectedPreset === preset.id ? 'primary' : 'default'}
                          onClick={() => applyPreset(preset.id)}
                          size="small"
                          variant={selectedPreset === preset.id ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                      <Chip
                        icon={<RestartAltIcon />}
                        label="Reset"
                        onClick={clearFilters}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </FloatingHeaderContainerDayCal>
            <ScheduledPatientsTable
              clinicBrandParam={'clinicBrandId'}
              clinicBrandIds={selectedClinicBrandId ? [selectedClinicBrandId] : []}
              clinicLocationParam={'clinicId'}
              clinicIds={selectedClinicId ? [selectedClinicId] : []}
              isEventTypesNotMandatory
              reportFetchingApiURL="/api/secure/teacher/scheduled-patients"
              fromInDayJsFormat={dayjs(selectedDateUtcStartTime)}
              toInDayJsFormat={dayjs(selectedDateUtcEndTime)}
              listingItemTransformer={(patientData) => {
                const brand = associatedClinicBrands?.find(
                  (cbr) => cbr?.id === patientData?.clinicBrandId,
                )
                const clinicLocation = mentorClinicAssociationData?.find(
                  (loc) => loc?.id === patientData?.clinicId,
                )
                const mentor = serviceProviders?.find((item) => item?.id === patientData?.mentorId)
                // console.log({ mentor, patientData })
                // console.log({ patientData, mentor })
                return {
                  ...patientData,
                  brandData: brand,
                  clinicData: clinicLocation,
                  mentorDetails: mentor,
                }
              }}
            />
          </div>
        </EDVCParent>
      </PageMainContentContainer>
    </>
  )
}
